import type { RunHistoryItem, RunSessionHistoryItem } from "./types";

const ACTIVE_RUN_STATUSES = new Set(["queued", "starting", "planning", "running", "awaiting_approval"]);

function timeValue(iso?: string | null): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

export function groupRunsIntoSessions(runs: RunHistoryItem[]): RunSessionHistoryItem[] {
  const grouped = new Map<string, RunHistoryItem[]>();
  for (const run of runs) {
    const sessionId = run.session_id || run.id;
    const list = grouped.get(sessionId) || [];
    list.push(run);
    grouped.set(sessionId, list);
  }

  return Array.from(grouped.entries())
    .map(([sessionId, sessionRuns]) => {
      const ordered = [...sessionRuns].sort((a, b) => timeValue(a.started_at) - timeValue(b.started_at));
      const latest = ordered.reduce((best, run) => (
        timeValue(run.started_at) >= timeValue(best.started_at) ? run : best
      ), ordered[0]);
      const first = ordered[0];
      const updatedAt = ordered.reduce((max, run) => Math.max(max, timeValue(run.finished_at) || timeValue(run.started_at)), 0);
      const openCount = ordered.filter((run) => ACTIVE_RUN_STATUSES.has(run.status)).length;
      return {
        id: sessionId,
        latestRunId: latest.id,
        title: first?.user_intent || latest.user_intent || "(no intent)",
        lane: latest.lane,
        status: openCount > 0 ? "running" : latest.status,
        started_at: first?.started_at || latest.started_at,
        updated_at: new Date(updatedAt || timeValue(latest.started_at)).toISOString(),
        finished_at: latest.finished_at,
        run_count: ordered.length,
        open_count: openCount,
        node_count: ordered.reduce((sum, run) => sum + (run.node_count || 0), 0),
        artifact_count: ordered.reduce((sum, run) => sum + (run.artifact_count || 0), 0),
        tool_calls: ordered.reduce((sum, run) => sum + (run.tool_calls || 0), 0),
        cost_usd: ordered.reduce((sum, run) => sum + (run.cost_usd || 0), 0),
        tokens_total: ordered.reduce((sum, run) => sum + (run.tokens_total || 0), 0),
        runs: ordered,
      };
    })
    // Order by the most recent run's START time — NOT updated_at. updated_at is
    // derived from finished_at, which is (re)set to "now" when a run is STOPPED,
    // so stopping an old conversation bumped its updated_at and made it float to
    // the top / jump out of order. started_at is stable: a session only moves
    // when a genuinely NEW message is sent in it. (runs are ordered asc by
    // started_at, so the last element is the latest.)
    .sort((a, b) => {
      const aLast = timeValue(a.runs[a.runs.length - 1]?.started_at);
      const bLast = timeValue(b.runs[b.runs.length - 1]?.started_at);
      return bLast - aLast;
    });
}
